import React, { useState, useEffect, useRef } from 'react';
import {
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
    CButton, CForm, CFormSelect, CFormCheck, CTabContent, CTabPane,
    CNav, CNavItem, CNavLink, CRow, CCol, CCard, CCardBody, CFormInput
} from '@coreui/react';
import { toast } from 'react-toastify';
import api from '../../../service/caller';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

const ScheduleModal = ({ visible, onClose, onScheduleUpdated, staff }) => {
    const [activeTab, setActiveTab] = useState('template');
    const [specialties, setSpecialties] = useState([]);
    const [selectedSpecialty, setSelectedSpecialty] = useState('');
    const [specialtyDurations, setSpecialtyDurations] = useState({});
    const [workDays, setWorkDays] = useState({
        monday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        tuesday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        wednesday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        thursday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        friday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        saturday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        sunday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
    });
    const [emergencySlots, setEmergencySlots] = useState([]);
    const [calendarView, setCalendarView] = useState('dayGridMonth');
    const [events, setEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableTimeSlots, setAvailableTimeSlots] = useState({});
    const [selectedEvent, setSelectedEvent] = useState(null);
    const calendarRef = useRef(null);
    const dayMap = {
        0: 'sunday',
        1: 'monday',
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
        6: 'saturday'
    };
    const [UserData, setUserData] = useState();

    const cleanTemplate = (template) => {
        const cleaned = {};
        Object.keys(template).forEach(day => {
            cleaned[day] = {
                ...template[day],
                active: Boolean(template[day].active), // Force boolean
                allowDoubleBooking: Boolean(template[day].allowDoubleBooking),
                specialtyId: template[day].specialtyId || null // Supprime si vide
            };
        });
        return cleaned;
    };
    useEffect(() => {
        if (staff) {
            const fetchLoggedUserData = async () => {
                try {
                    const response = await api.get('api/usersprofile');
                    setUserData(response.data);
                } catch (error) {
                    toast.error("Failed to fetch user data");
                }
            };
            fetchLoggedUserData();
            fetchStaffSpecialties();
            fetchStaffSchedule();
        }
    }, [staff]);

    useEffect(() => {
        if (selectedSpecialty && activeTab === 'calendar') {
            generateCalendarEvents();
        }
    }, [selectedSpecialty, workDays, emergencySlots, activeTab]);

    const fetchStaffSpecialties = async () => {
        try {
            const staffSpecialties = staff?.specialties || [];
            setSpecialties(staffSpecialties);
            if (staffSpecialties.length > 0) {
                setSelectedSpecialty(staffSpecialties[0]._id);
            }
            const initialDurations = {};
            staffSpecialties.forEach(specialty => {
                initialDurations[specialty._id] = 30;
            });
            setSpecialtyDurations(initialDurations);
        } catch (error) {
            console.error("Error fetching specialties:", error);
            toast.error("Failed to load specialties");
        }
    };

    const fetchStaffSchedule = async () => {
        try {
            const response = await api.get(`api/newschedule/${staff?._id}`);
            if (response.data) {
                if (response.data.template) {
                    const processedTemplate = {};
                    Object.entries(response.data.template).forEach(([day, schedule]) => {
                        processedTemplate[day] = {
                        ...schedule,
                        specialtyId: schedule.specialtyId?._id || null
                        };
                    });
                    setWorkDays(processedTemplate);
                }
                if (response.data.emergencySlots) {
                    const processedEmergencySlots = response.data.emergencySlots.map(slot => ({
                        ...slot,
                        specialtyId: slot.specialtyId?._id || null // Extraction de l'ID
                    }));
                    setEmergencySlots(processedEmergencySlots);
                }
                if (response.data.specialtyDurations) setSpecialtyDurations(response.data.specialtyDurations);
                if (response.data.events) {
                    const customEvents = response.data.events.map(event => ({
                        id: event._id,
                        title: event.title,
                        start: event.start,
                        end: event.end,
                        backgroundColor: getColorByPriority(event.priority),
                        extendedProps: {
                            type: event.type,
                            priority: event.priority,
                            specialty: event.specialty,
                            isCustom: true
                        }
                    }));
                    setEvents(customEvents);
                }
            }
        } catch (error) {
            console.error("Error fetching schedule:", error);
            toast.error("Failed to load schedule");
        }
    };

    const getColorByPriority = (priority) => {
        return priority === 'emergency' ? '#ffc107' : '#28a745';
    };

    const isEmergencyConflict = (emergencyDate, emergencyStart, emergencyEnd) => {
        const date = new Date(emergencyDate);
        const dayName = dayMap[date.getDay()];
        const workDay = workDays[dayName];
        if (!workDay?.active) return false;

        // Create local time Date objects for comparison
        const [startHour, startMinute] = workDay.start.split(':').map(Number);
        const [endHour, endMinute] = workDay.end.split(':').map(Number);
        const [emergencyStartHour, emergencyStartMinute] = emergencyStart.split(':').map(Number);
        const [emergencyEndHour, emergencyEndMinute] = emergencyEnd.split(':').map(Number);

        // Create date objects in local time
        const templateStart = new Date(date);
        templateStart.setHours(startHour, startMinute, 0);

        const templateEnd = new Date(date);
        templateEnd.setHours(endHour, endMinute, 0);

        const emergencyStartTime = new Date(date);
        emergencyStartTime.setHours(emergencyStartHour, emergencyStartMinute, 0);

        const emergencyEndTime = new Date(date);
        emergencyEndTime.setHours(emergencyEndHour, emergencyEndMinute, 0);

        return emergencyStartTime < templateEnd && emergencyEndTime > templateStart;
    };

    const generateCalendarEvents = () => {
        const newEvents = [];
        const dayNameToIndex = {
            sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
            thursday: 4, friday: 5, saturday: 6
        };

        Object.entries(workDays).forEach(([day, schedule]) => {
            if (schedule.active && schedule.specialtyId) {
                const duration = specialtyDurations[schedule.specialtyId] || 30;

                // Calculate 7 months ahead (approximately 30 weeks)
                const weeksToShow = 30;

                for (let weekOffset = 0; weekOffset < weeksToShow; weekOffset++) {
                    // Start from the current day plus weekOffset weeks
                    const date = new Date();
                    date.setDate(date.getDate() + weekOffset * 7);

                    // Find the next occurrence of the target day
                    const dayIndex = dayNameToIndex[day];
                    while (date.getDay() !== dayIndex) {
                        date.setDate(date.getDate() + 1);
                    }

                    // Parse start and end times
                    const [startHour, startMinute] = schedule.start.split(':').map(Number);
                    const [endHour, endMinute] = schedule.end.split(':').map(Number);

                    // Create date objects in local time
                    const dayStart = new Date(date);
                    dayStart.setHours(startHour, startMinute, 0);

                    const dayEnd = new Date(date);
                    dayEnd.setHours(endHour, endMinute, 0);

                    let currentStart = new Date(dayStart);
                    while (currentStart < dayEnd) {
                        const currentEnd = new Date(currentStart);
                        currentEnd.setMinutes(currentStart.getMinutes() + duration);

                        if (currentEnd > dayEnd) break;

                        newEvents.push({
                            title: "Available Slot",
                            start: new Date(currentStart).toISOString(), // Store in UTC but will be displayed in local time
                            end: new Date(currentEnd).toISOString(),
                            allDay: false,
                            color: schedule.allowDoubleBooking ? '#2979ff' : '#28a745',
                            extendedProps: {
                                type: 'regular',
                                allowDoubleBooking: schedule.allowDoubleBooking,
                                specialtyId: schedule.specialtyId
                            }
                        });

                        currentStart = currentEnd;
                    }
                }
            }
        });

        // Emergency slots
        emergencySlots.forEach(slot => {
            newEvents.push({
                title: "Emergency Slot",
                start: `${slot.date}T${slot.start}`,
                end: `${slot.date}T${slot.end}`,
                color: '#dc3545',
                extendedProps: { type: 'emergency' }
            });
        });

        setEvents(newEvents);
    };

    const isDateAvailable = (date) => {
        // Compare local dates
        const clickedDateLocal = new Date(date);
        clickedDateLocal.setHours(0, 0, 0, 0);

        return events.some(event => {
            const eventDateLocal = new Date(event.start);
            eventDateLocal.setHours(0, 0, 0, 0);
            return eventDateLocal.getTime() === clickedDateLocal.getTime();
        });
    };

    const handleWorkDayChange = (day, field, value) => {
        setWorkDays(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: (field === 'active' || field === 'allowDoubleBooking')
                    ? value // Ce sera un boolean (e.target.checked)
                    : value // Les autres champs comme start/end/specialtyId restent tels quels
            }
        }));
    };

    const handleDurationChange = (specialtyId, duration) => {
        setSpecialtyDurations(prev => ({
            ...prev,
            [specialtyId]: parseInt(duration)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const customEvents = events.filter(event => event.extendedProps?.isCustom).map(event => ({
                title: event.title,
                start: event.start,
                end: event.end,
                type: event.extendedProps?.type || 'regular',
                priority: event.extendedProps?.priority || 'normal',
                specialty: event.extendedProps?.specialty._id || selectedSpecialty._id
            }));

            const scheduleData = {
                staffId: staff._id,
                template: cleanTemplate(workDays),
                specialtyId: selectedSpecialty,
                emergencySlots,
                specialtyDurations,
                events: customEvents
            };

            const response1 = await api.get(`api/newschedule/${staff._id}`);
            if (response1?.data?._id) {
                await api.put(`api/newschedule/${response1.data._id}`, scheduleData);
                toast.success("Schedule updated successfully!");
            } else {
                await api.post('api/newschedule', scheduleData);
                toast.success("New schedule created successfully!");
            }

            onScheduleUpdated();
            resetForm();
        } catch (error) {
            console.error("Error saving schedule:", error);
            toast.error("An error occurred while saving the schedule");
        }
    };

    const resetForm = () => {
        setWorkDays({
            monday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            tuesday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            wednesday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            thursday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            friday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            saturday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
            sunday: { active: false, start: '09:00', end: '17:00', allowDoubleBooking: false, specialtyId: '' },
        });
    };

    const handleEventDrop = (info) => {
        const { event } = info;
        if (event.extendedProps.isCustom) {
            setEvents(prevEvents => prevEvents.map(evt =>
                evt.id === event.id
                    ? { ...evt, start: event.start, end: event.end }
                    : evt
            ));
            toast.info("Event moved: " + event.title);
        } else {
            info.revert();
            toast.warning("Template events cannot be moved. Create a custom event instead.");
        }
    };

    const handleDateClick = (dateClickInfo) => {
        const clickedDate = dateClickInfo.dateStr;
        setSelectedDate(clickedDate);

        const dateEvents = events.filter(event => {
            const eventDate = new Date(event.start).toISOString().split('T')[0];
            return eventDate === clickedDate;
        }).sort((a, b) => new Date(a.start) - new Date(b.start));

        const formattedSlots = dateEvents.map(event => {
            const specialty = specialties.find(s => s._id === event.extendedProps.specialtyId);
            return {
                time: new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                endTime: new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                specialtyName: specialty?.name || '',
                type: event.extendedProps.type,
                allowDoubleBooking: event.extendedProps.allowDoubleBooking
            };
        });

        setAvailableTimeSlots(prev => ({
            ...prev,
            [clickedDate]: formattedSlots
        }));
    };

    const handleSpecialtyChange = (e) => {
        setSelectedSpecialty(e.target.value);
    };

    const handleAddEmergencySlot = () => {
        const now = new Date();
        const newSlot = {
            id: `emergency-${Date.now()}`,
            date: now.toISOString().split('T')[0],
            start: '08:00',
            end: '09:00',
            specialtyId: ''
        };
        setEmergencySlots(prev => [...prev, newSlot]);
    };

    const handleRemoveEmergencySlot = (id) => {
        setEmergencySlots(prev => prev.filter(slot => slot.id !== id));
    };

    const handleEmergencySlotChange = (id, field, value) => {
        setEmergencySlots(prev =>
            prev.map(slot =>
                slot.id === id
                    ? { ...slot, [field]: value }
                    : slot
            )
        );
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'calendar' && selectedSpecialty) {
            generateCalendarEvents();
            if (calendarRef.current) {
                const api = calendarRef.current.getApi();
                api.changeView(calendarView);
            }
        }
    };

    return (
        <CModal visible={visible} onClose={onClose} size="xl">
            <CModalHeader>
                <CModalTitle>
                    Schedule Management - {staff?.firstname} {staff?.lastname}
                </CModalTitle>
            </CModalHeader>
            <CModalBody>
                {/* <CNav variant="tabs" className="mb-3">
                    <CNavItem>
                        <CNavLink
                            active={activeTab === 'template'}
                            onClick={() => handleTabChange('template')}
                        >
                            Weekly Template
                        </CNavLink>
                    </CNavItem>
                    <CNavItem>
                        <CNavLink
                            active={activeTab === 'calendar'}
                            onClick={() => handleTabChange('calendar')}
                        >
                            Calendar View
                        </CNavLink>
                    </CNavItem>

                    {(staff?.role?.name === "medtinz_client_doctor") && (
                        <>
                            <CNavItem>
                                <CNavLink
                                    active={activeTab === 'emergency'}
                                    onClick={() => handleTabChange('emergency')}
                                >
                                    Emergency Slots
                                </CNavLink>
                            </CNavItem>
                            <CNavItem>
                                <CNavLink
                                    active={activeTab === 'durations'}
                                    onClick={() => handleTabChange('durations')}
                                >
                                    Durée des RDVs
                                </CNavLink>
                            </CNavItem>
                        </>
                    )}
                </CNav> */}

                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'template' ? 'active' : ''}`}
                            onClick={() => handleTabChange('template')}
                            type="button"
                        >
                            Weekly Template
                        </button>
                    </li>

                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'calendar' ? 'active' : ''}`}
                            onClick={() => handleTabChange('calendar')}
                            type="button"
                        >
                            Calendar View
                        </button>
                    </li>

                    {staff?.role?.name === "medtinz_client_doctor" && (
                        <>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'emergency' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('emergency')}
                                    type="button"
                                >
                                    Emergency Slots
                                </button>
                            </li>

                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'durations' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('durations')}
                                    type="button"
                                >
                                    Durée des RDVs
                                </button>
                            </li>
                        </>
                    )}
                </ul>

                <CTabContent>
                    {/* Template Tab */}
                    <CTabPane visible={activeTab === 'template'}>
                        <CForm onSubmit={handleSubmit}>
                            <h5 className="mt-4 mb-3">Weekly Schedule Template</h5>
                            {Object.entries(workDays).map(([day, schedule]) => (
                                <div className='row'>
                                    <div className='col-lg-2 align-self-center mb-3'>
                                    <CFormCheck
                                        id={`${day}-active`}
                                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                                        checked={schedule.active}
                                        onChange={(e) => handleWorkDayChange(day, 'active', e.target.checked)}
                                    />
                                    </div>
                                    <div className='col-lg-3 mb-3'>
                                        <label htmlFor={`${day}-start`}>Start Time</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            id={`${day}-start`}
                                            value={schedule.start}
                                            onChange={(e) => handleWorkDayChange(day, 'start', e.target.value)}
                                            disabled={!schedule.active}
                                        />
                                    </div>
                                    <div className='col-lg-3 mb-3'>
                                        <label htmlFor={`${day}-end`}>End Time</label>
                                        <input
                                            type="time"
                                            className="form-control"
                                            id={`${day}-end`}
                                            value={schedule.end}
                                            onChange={(e) => handleWorkDayChange(day, 'end', e.target.value)}
                                            disabled={!schedule.active}
                                        />
                                    </div>
                                    <div className='col-lg-3 mb-3'>
                                        {(staff?.role?.name === "medtinz_client_doctor" || staff?.role?.name === "medtinz_client_nurses") && (
                                            <>
                                                <label htmlFor={`${day}-specialty`}>Specialty</label>
                                                <CFormSelect
                                                    id={`${day}-specialty`}
                                                    value={schedule.specialtyId || ''}
                                                    onChange={(e) => handleWorkDayChange(day, 'specialtyId', e.target.value)}
                                                    disabled={!schedule.active}
                                                >
                                                    <option value="">Select a specialty</option>
                                                    {specialties.map(specialty => (
                                                        <option key={specialty._id} value={specialty._id}>
                                                            {specialty.name}
                                                        </option>
                                                    ))}
                                                </CFormSelect>
                                            </>
                                        )}

                                    </div>
                                    <div className='col-lg-3 mb-3'>
                                        {staff?.role?.name === "medtinz_client_doctor" && (
                                            <CFormCheck
                                                id={`${day}-double-booking`}
                                                label="Allow Double Booking"
                                                checked={schedule.allowDoubleBooking}
                                                onChange={(e) => handleWorkDayChange(day, 'allowDoubleBooking', e.target.checked)}
                                                disabled={!schedule.active}
                                            />
                                        )}
                                    </div>
                                </div>


                                // <CRow key={day} className="mb-3 align-items-center">
                                //     <CCol md={2}>
                                //         <CFormCheck
                                //             id={`${day}-active`}
                                //             label={day.charAt(0).toUpperCase() + day.slice(1)}
                                //             checked={schedule.active}
                                //             onChange={(e) => handleWorkDayChange(day, 'active', e)}
                                //         />
                                //     </CCol>
                                //     <CCol md={2}>
                                //         <label htmlFor={`${day}-start`}>Start Time</label>
                                //         <input
                                //             type="time"
                                //             className="form-control"
                                //             id={`${day}-start`}
                                //             value={schedule.start}
                                //             onChange={(e) => handleWorkDayChange(day, 'start', e.target.value)}
                                //             disabled={!schedule.active}
                                //         />
                                //     </CCol>
                                //     <CCol md={2}>
                                //         <label htmlFor={`${day}-end`}>End Time</label>
                                //         <input
                                //             type="time"
                                //             className="form-control"
                                //             id={`${day}-end`}
                                //             value={schedule.end}
                                //             onChange={(e) => handleWorkDayChange(day, 'end', e.target.value)}
                                //             disabled={!schedule.active}
                                //         />
                                //     </CCol>
                                //     {(staff?.role?.name === "medtinz_client_doctor" || staff?.role?.name === "medtinz_client_nurses") && (
                                //         <CCol md={3}>
                                //             <label htmlFor={`${day}-specialty`}>Specialty</label>
                                //             <CFormSelect
                                //                 id={`${day}-specialty`}
                                //                 value={schedule.specialtyId?._id || ''}
                                //                 onChange={(e) => handleWorkDayChange(day, 'specialtyId', e.target.value)}
                                //                 disabled={!schedule.active}
                                //             >
                                //                 <option value="">Select a specialty</option>
                                //                 {specialties.map(specialty => (
                                //                     <option key={specialty._id} value={specialty._id}>
                                //                         {specialty.name}
                                //                     </option>
                                //                 ))}
                                //             </CFormSelect>
                                //         </CCol>
                                //     )}
                                //     {staff?.role?.name === "medtinz_client_doctor" && (
                                //         <CCol md={3}>
                                //             <CFormCheck
                                //                 id={`${day}-double-booking`}
                                //                 label="Allow Double Booking"
                                //                 checked={schedule.allowDoubleBooking}
                                //                 onChange={(e) => handleWorkDayChange(day, 'allowDoubleBooking', e)}
                                //                 disabled={!schedule.active}
                                //             />
                                //         </CCol>
                                //     )}
                                // </CRow>
                            ))}
                        </CForm>
                    </CTabPane>

                    {/* Calendar Tab */}
                    <CTabPane visible={activeTab === 'calendar'}>
                        {/* <CRow className="mb-3">
                            <CCol md={6}>
                                <label htmlFor="calendar-view">Calendar View</label>
                                <CFormSelect 
                                    id="calendar-view"
                                    value={calendarView}
                                    onChange={(e) => setCalendarView(e.target.value)}
                                    className="mb-3"
                                >
                                    <option value="dayGridMonth">Monthly View</option>
                                    <option value="timeGridWeek">Weekly View</option>
                                    <option value="timeGridDay">Daily View</option>
                                </CFormSelect>
                            </CCol>
                        </CRow> */}

                        <div className="mb-3">
                            <CButton
                                color="primary"
                                onClick={generateCalendarEvents}
                                className="me-2"
                            >
                                Refresh Calendar
                            </CButton>
                            <small className="text-muted">
                                Click to regenerate the calendar based on template and emergency slots
                            </small>
                        </div>

                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView={calendarView}
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth'
                            }}
                            // events={events}
                            editable={false}
                            selectable={true}
                            selectMirror={true}
                            dayMaxEvents={true}
                            height="auto"
                            dateClick={handleDateClick}
                            eventDrop={handleEventDrop}
                            dayCellDidMount={(info) => {
                                const hasSlots = isDateAvailable(info.date);
                                if (hasSlots) {
                                    info.el.style.backgroundColor = '#e8f5e9';
                                    info.el.style.cursor = 'pointer';
                                } else {
                                    info.el.style.backgroundColor = '#f5f5f5';
                                    info.el.style.color = '#aaa';
                                    info.el.style.cursor = 'not-allowed';
                                    info.el.classList.add('disabled-date');
                                }
                            }}
                        />
                        {/* Section affichant les créneaux horaires */}
                        {selectedDate && availableTimeSlots[selectedDate] && (
                            <div className="mt-4">
                                <h5>Available Time Slots for {new Date(selectedDate).toLocaleDateString()}</h5>
                                <div className="row">
                                    {availableTimeSlots[selectedDate].length > 0 ? (
                                        availableTimeSlots[selectedDate].map((slot, index) => (
                                            <div key={index} className="col-md-3 mb-2">
                                                <div className="card p-2" style={{
                                                    borderLeft: `4px solid ${slot.type === 'emergency' ? '#dc3545' :
                                                        slot.allowDoubleBooking ? '#2979ff' : '#28a745'}`
                                                }}>
                                                    <small>{slot.time} - {slot.endTime}</small>
                                                    <strong>{slot.specialtyName}</strong>
                                                    {slot.type === 'emergency' && (
                                                        <span className="badge bg-danger">Emergency</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="alert alert-info">No available slots for this date</div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="mt-3">
                            <h6>Color Legend:</h6>
                            <div className="d-flex flex-wrap gap-3">
                                <div className="d-flex align-items-center">
                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#28a745', marginRight: '5px' }}></div>
                                    <small>Regular slots</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#2979ff', marginRight: '5px' }}></div>
                                    <small>Double-bookable slots</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div style={{ width: '15px', height: '15px', backgroundColor: '#dc3545', marginRight: '5px' }}></div>
                                    <small>Emergency slots</small>
                                </div>
                            </div>
                        </div>
                    </CTabPane>

                    {/* Emergency Tab */}
                    <CTabPane visible={activeTab === 'emergency'}>
                        <CRow className="mb-3">
                            <CCol md={12}>
                                <CButton
                                    color="primary"
                                    onClick={handleAddEmergencySlot}
                                    className="mb-3"
                                >
                                    Add Emergency Slot
                                </CButton>
                                {emergencySlots.map((slot, index) => (
                                    <CCard key={slot.id} className="mb-3">
                                        <CCardBody>
                                            <CRow className="align-items-center">
                                                <CCol md={2}>
                                                    <label>Date</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={slot.date}
                                                        onChange={(e) => handleEmergencySlotChange(slot.id, 'date', e.target.value)}
                                                    />
                                                </CCol>
                                                <CCol md={2}>
                                                    <label>Start Time</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={slot.start}
                                                        onChange={(e) => {
                                                            const newValue = e.target.value;
                                                            if (!isEmergencyConflict(slot.date, newValue, slot.end)) {
                                                                handleEmergencySlotChange(slot.id, 'start', newValue);
                                                            } else {
                                                                toast.error("Emergency slot can't overlap with working hours!");
                                                            }
                                                        }}
                                                    />
                                                </CCol>
                                                <CCol md={2}>
                                                    <label>End Time</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={slot.end}
                                                        onChange={(e) => {
                                                            const newValue = e.target.value;
                                                            if (!isEmergencyConflict(slot.date, slot.start, newValue)) {
                                                                handleEmergencySlotChange(slot.id, 'end', newValue);
                                                            } else {
                                                                toast.error("Emergency slot can't overlap with working hours!");
                                                            }
                                                        }}
                                                    />
                                                </CCol>
                                                <CCol md={3}>
                                                    <label>Specialty</label>
                                                    <CFormSelect
                                                        value={slot.specialtyId || ''}
                                                        onChange={(e) => handleEmergencySlotChange(slot.id, 'specialtyId', e.target.value)}
                                                    >
                                                        <option value="">Select a specialty</option>
                                                        {specialties.map(specialty => (
                                                            <option key={specialty._id} value={specialty._id}>
                                                                {specialty.name}
                                                            </option>
                                                        ))}
                                                    </CFormSelect>
                                                </CCol>
                                                <CCol md={3} className="d-flex align-items-end">
                                                    <CButton
                                                        color="danger"
                                                        onClick={() => handleRemoveEmergencySlot(slot.id)}
                                                    >
                                                        Remove
                                                    </CButton>
                                                </CCol>
                                            </CRow>
                                        </CCardBody>
                                    </CCard>
                                ))}
                            </CCol>
                        </CRow>
                    </CTabPane>

                    {/* Durations Tab */}
                    <CTabPane visible={activeTab === 'durations'}>
                        <CRow>
                            <CCol md={12}>
                                <h5 className="mb-4">Appointment Duration by Specialty</h5>
                                {specialties.length > 0 ? (
                                    specialties.map(specialty => (
                                        <CCard key={specialty._id} className="mb-3">
                                            <CCardBody>
                                                <CRow className="align-items-center">
                                                    <CCol md={6}>
                                                        <strong>{specialty.name}</strong>
                                                    </CCol>
                                                    <CCol md={6}>
                                                        <CRow className="align-items-center">
                                                            <CCol md={8}>
                                                                <CFormInput
                                                                    type="number"
                                                                    min="5"
                                                                    max="240"
                                                                    step="5"
                                                                    value={specialtyDurations[specialty._id] || 30}
                                                                    onChange={(e) => handleDurationChange(specialty._id, e.target.value)}
                                                                    label="Duration (minutes)"
                                                                />
                                                            </CCol>
                                                            <CCol md={4}>
                                                                <span>minutes</span>
                                                            </CCol>
                                                        </CRow>
                                                    </CCol>
                                                </CRow>
                                            </CCardBody>
                                        </CCard>
                                    ))
                                ) : (
                                    <div className="alert alert-info">
                                        No specialties configured for this staff member.
                                    </div>
                                )}
                            </CCol>
                        </CRow>
                    </CTabPane>
                </CTabContent>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" onClick={onClose}>
                    Close
                </CButton>
                <CButton
                    color="primary"
                    disabled={!(
                        UserData?.role?.name === "medtinz_client_master" ||
                        staff?._id === UserData?._id
                    )}
                    onClick={handleSubmit}
                >
                    Save Schedule
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default ScheduleModal;